/**
 * useSavedCard — read the user's saved default payment method.
 *
 * in-app-checkout Phase 3 Session 3.5.
 *
 * Calls `GET /api/billing/payment-method` which returns the Stripe default
 * payment method's card projection nested under `{ payment_method: ... }`,
 * or `{ payment_method: null }` when no default payment method is on file.
 *
 * Wire shape returned by `GET /api/billing/payment-method`:
 *   {
 *     payment_method: {
 *       id: string;
 *       brand: string;
 *       last4: string;
 *       exp_month: number;
 *       exp_year: number;
 *     } | null
 *   }
 *
 * The API wraps this in the standard `{ success: true, data: { payment_method } }`
 * envelope, so the full structure is `envelope.data.payment_method`.
 *
 * Design:
 *   - Only enabled when `hasStripeCustomer` is true (callers gate on
 *     `!!row.stripe_customer_id`).
 *   - Takes `userId: string | undefined` so callers can pass `user?.id`
 *     from `useAuth()` externally — following the hook-arg convention for
 *     useAuth-derived state (apps/web/CLAUDE.md § 2026-05-13 footnote).
 *   - `staleTime: 120_000` — card brand/last4 rarely changes; 2-minute
 *     cache avoids a round-trip on every hub mount without going stale.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** Saved payment method card projection returned by `GET /api/billing/payment-method`. */
export interface SavedCard {
  /** Stripe PaymentMethod id. */
  id?: string;
  /** Card network brand (e.g. 'visa', 'mastercard', 'amex', 'discover'). */
  brand: string;
  /** Last 4 digits of the card number. */
  last4: string;
  /** Expiry month (1–12). */
  exp_month: number;
  /** Expiry year (4-digit). */
  exp_year: number;
}

/**
 * The API endpoint returns `{ payment_method: SavedCard | null }` as its data
 * payload inside the standard `{ success, data }` envelope.
 */
interface PaymentMethodData {
  payment_method: SavedCard | null;
}

interface SavedCardEnvelope {
  data: PaymentMethodData;
}

/** Request body for `POST /api/billing/payment-method`. */
interface UpdatePaymentMethodBody {
  /** Stripe PaymentMethod id confirmed via `confirmSetup`. */
  payment_method_id: string;
}

interface UpdatePaymentMethodEnvelope {
  data: { payment_method_id: string };
}

/**
 * useUpdatePaymentMethod — attach + set default payment method.
 *
 * Calls `POST /api/billing/payment-method` with the confirmed `payment_method_id`
 * from a `confirmSetup` call. On success, invalidates the `savedCard` cache key
 * (user-scoped) so the card row reflects the new default immediately.
 *
 * Takes `userId` as an argument (same hook-arg convention as `useSavedCard`)
 * so it doesn't call `useAuth` internally and avoids circular imports.
 */
export function useUpdatePaymentMethod(userId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<
    UpdatePaymentMethodEnvelope,
    Error,
    UpdatePaymentMethodBody
  >({
    mutationFn: async ({ payment_method_id }) => {
      return api.post<UpdatePaymentMethodEnvelope>(
        '/api/billing/payment-method',
        { payment_method_id }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.billing.savedCard(userId),
      });
    },
    retry: 0,
  });
}

export function useSavedCard(
  userId: string | undefined,
  hasStripeCustomer: boolean
) {
  const query = useQuery<SavedCard | null>({
    queryKey: queryKeys.billing.savedCard(userId),
    queryFn: async () => {
      const envelope = await api.get<SavedCardEnvelope>(
        '/api/billing/payment-method'
      );
      // The API returns { success, data: { payment_method: SavedCard | null } }
      // Unwrap the nested payment_method from within data.
      return envelope.data.payment_method;
    },
    // Only fetch for Stripe customers with a known user id.
    enabled: !!userId && hasStripeCustomer,
    staleTime: 120_000,
  });

  return {
    savedCard: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
